import React, { useEffect, useRef } from 'react';
import { Box, Tabs, Tab, Divider, Typography, Button, IconButton, ButtonBase } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronUp, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { WithSmallScreen } from '../../common/WithSmallScreen';

export const TrackingPanel = WithSmallScreen(
  ({
    tab,
    selectedIssueId,
    issues,
    dismissAll,
    undoAll,
    setPopoverOpen,
    setSelectedAnchorEl,
    busy,
    file,
    selectIssue,
    setTab,
    issueNumberById,
    highlightMapRef,
    previewShort,
    acceptAll,
    isLoading,
    minimized,
    onMinimizeToggle,
    isSmall,
    isMD,
    isLG,
  }) => {
    const listRef = useRef(null);
    const isSmallerScreen = isSmall || isMD || isLG;
    const visibleSuggestions = issues.filter(
      (issue) => issue.status === 'open' || issue.status === 'stale',
    );
    const openSuggestionCount = issues.filter((issue) => issue.status === 'open').length;
    const acceptedIssues = issues.filter((issue) => issue.status === 'accepted');
    const displayedIssues = tab === 'suggestions' ? visibleSuggestions : acceptedIssues;
    const isBulkActionDisabled = !file || busy || isLoading;

    // Auto-scroll the selected issue card into view in the tracking panel
    useEffect(() => {
      if (!selectedIssueId || !listRef.current) return;
      const card = listRef.current.querySelector(`[data-tracking-id="${selectedIssueId}"]`);
      if (card) {
        card.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }, [selectedIssueId]);

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          bgcolor: 'background.paper',
          overflow: 'hidden',
        }}
      >
        {/* ── Minimize / Restore bar (mobile only) ── */}
        {isSmallerScreen && (
          <Box
            onClick={() => onMinimizeToggle((prev) => !prev)}
            sx={{
              flex: '0 0 auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              py: 0.5,
              cursor: 'pointer',
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <IconButton size="small" tabIndex={-1}>
              <FontAwesomeIcon icon={minimized ? faChevronUp : faChevronDown} size="xs" />
            </IconButton>
          </Box>
        )}

        {/* ── Panel content (hidden on mobile when minimized) ── */}
        {(!isSmallerScreen || !minimized) && (
          <>
            {/* ── Pinned top: Tabs ── */}
            <Box sx={{ flex: '0 0 auto' }}>
              <Tabs
                sx={{ pl: 1, pr: 1 }}
                value={tab}
                onChange={(_, v) => setTab(v)}
                variant="fullWidth"
                textColor="primary"
                indicatorColor="primary"
              >
                <Tab label="Suggestions" value="suggestions" />
                <Tab label="Changes" value="changes" />
              </Tabs>
              <Divider />
            </Box>

            {/* ── Scrollable middle: Issue list ── */}
            <Box
              ref={listRef}
              sx={{
                flex: '1 1 auto',
                overflowY: 'auto',
                p: 1.5,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}
            >
              {displayedIssues.map((it) => {
                const num = issueNumberById.get(it.id);
                return (
                  <ButtonBase
                    component="button"
                    type="button"
                    key={it.id}
                    data-tracking-id={it.id}
                    className={`trackingPanelItem ${selectedIssueId === it.id ? 'selected' : ''} ${
                      it.status === 'stale' ? 'stale' : ''
                    }`}
                    aria-label={`${tab === 'suggestions' ? 'Open suggestion' : 'Open applied change'} ${num ? `${num}: ` : ''}${it.rule}`}
                    onClick={() => {
                      selectIssue(it.id);
                      if (!highlightMapRef.current.get(it.id)) {
                        setSelectedAnchorEl(null);
                        setPopoverOpen(false);
                      }
                    }}
                  >
                    <Typography variant="body2" className="trackingPanelItemTitle">
                      {tab === 'suggestions' && num ? `${num}. ` : ''}
                      {it.rule}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {previewShort(it.message, 90)}
                    </Typography>

                    <Typography variant="caption" className="trackingPanelItemMeta">
                      {it.status === 'stale'
                        ? 'Stale: overlaps an accepted change. Re-run analysis to refresh.'
                        : tab === 'changes'
                          ? `Inserted: ${previewShort(it.suggested, 45)}`
                          : `Replace: ${previewShort(it.original, 45)} → ${previewShort(it.suggested, 45)}`}
                    </Typography>
                  </ButtonBase>
                );
              })}

              {displayedIssues.length === 0 && (
                <Typography variant="caption" color="text.secondary">
                  No items yet.
                </Typography>
              )}
            </Box>

            {/* ── Pinned bottom: Action buttons ── */}
            <Box sx={{ flex: '0 0 auto' }}>
              <Divider />
              <Box sx={{ p: 1.5 }}>
                {tab === 'suggestions' ? (
                  <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
                    <Button
                      variant="outlined"
                      size="small"
                      fullWidth
                      onClick={acceptAll}
                      disabled={isBulkActionDisabled || openSuggestionCount === 0}
                    >
                      Accept All
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      fullWidth
                      onClick={dismissAll}
                      disabled={isBulkActionDisabled || visibleSuggestions.length === 0}
                    >
                      Dismiss All
                    </Button>
                  </Box>
                ) : (
                  <Button
                    variant="outlined"
                    size="small"
                    fullWidth
                    onClick={undoAll}
                    disabled={isBulkActionDisabled || acceptedIssues.length === 0}
                  >
                    Undo All
                  </Button>
                )}
              </Box>
            </Box>
          </>
        )}
      </Box>
    );
  },
);
